import { useFragment } from '@apollo/client';
import { Alert, Button, Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { PersonalizeSidePanelButton } from 'src/components/SidePanelPersonalization/personalize-side-panel-button';
import { resolvePartnerTabContactInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-contact-info';
import { PartnerInfo } from 'src/components/SidePanelTabs/PartnerTab/partner-info';
import { PartnerInfoTabs } from 'src/components/SidePanelTabs/PartnerTab/partner-info-tabs';
import { ClientCard } from 'src/components/SidePanelTabs/SiteClient/client-card';
import { gql } from 'src/gql';
import { Client, useGroupRules, useRestoreClient, UserGroupRules } from 'src/graphql';
import { ClientDetailsTabs } from './client-details-tabs';
import { ClientSites } from './client-sites';

interface TabContentProps {
  clientId: string;
  activeTab: ClientDetailsTabs;
  extendedView?: boolean;
}

const ClientTabContentFragment = gql(`
  fragment ClientTabContent_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    isDeleted @client
    deletedAt
    primaryEmail {
      id
      value
    }
    partner {
      id
      partnerId
      supportPhone
      supportEmail
      membershipEmail
      supportLiveDate
      litsDate
      partnerOnesheet {
        id
        partnerId
        mainOfficePhone
        asTransferForBusinessHoursOnly
        internalSupportNotes
        escalationPolicy
        recentAnnouncements
        whatIsYourEquipmentRepairPolicy
        primaryRsmTool
      }
      brandGroups {
        id
        brandGroupId
        fwdPhonePublished
        fwdPhonePublishedExt
        fwdPhoneSupport
        supportEmail
        membershipEmail
      }
    }
    ...ClientCard_Client
  }
`);

export const ClientTabContent = ({ activeTab, clientId, extendedView }: TabContentProps) => {
  const { checkValidity } = useGroupRules();
  const { restoreClient } = useRestoreClient();

  const { data: client, complete } = useFragment({
    fragment: ClientTabContentFragment,
    fragmentName: 'ClientTabContent_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const onRestoreClientClick = useCallback(async () => {
    await restoreClient(clientId);
  }, [restoreClient, clientId]);

  // TODO(gql-migrate): ClientSites still consumes the legacy mapped Client shape;
  // drop this bridge once it reads the cache via useFragment itself.
  const legacyClient = useMemo(
    () =>
      ({
        id: client.id,
        clientId,
        partnerId: client.partnerId,
        firstName: client.firstName,
        lastName: client.lastName,
        fullName: client.fullName,
        primaryEmail: client.primaryEmail?.value,
        deletedAt: client.deletedAt,
      }) as Client,
    [client, clientId],
  );

  const partnerWithBrandGroupContactInfo = useMemo(
    () => complete ? resolvePartnerTabContactInfo(client.partner) : null,
    [client.partner, complete],
  );

  if (!complete) {
    return null;
  }

  if (activeTab === ClientDetailsTabs.Client) {
    return (
      <>
        <ClientCard
          clientId={clientId}
          sx={{
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            overflow: 'visible',
          }}
          showClientMenu={!extendedView}
          showMoreButton={!extendedView}
          headerAction={<PersonalizeSidePanelButton />}
        />
        {client.isDeleted && !extendedView ? (
          <Alert
            severity="info"
            action={
              checkValidity([UserGroupRules.AccessToRestoreClients]) ? (
                <Button color="inherit" size="small" onClick={onRestoreClientClick}>
                  RESTORE
                </Button>
              ) : null
            }
          >
            This client was deleted
          </Alert>
        ) : null}
        {!extendedView && <ClientSites client={legacyClient} />}
      </>
    );
  }

  if (activeTab === ClientDetailsTabs.Partner) {
    return (
      <Stack spacing={2}>
        <PartnerInfo partner={partnerWithBrandGroupContactInfo} />
        {partnerWithBrandGroupContactInfo ? (
          <PartnerInfoTabs partner={partnerWithBrandGroupContactInfo} />
        ) : null}
      </Stack>
    );
  }

  return null;
};

